// Rotina diária montada a partir dos óleos selecionados na aba
// "Protocolos de Óleos Essenciais".
//
// Regras:
// - Sem IA e sem backend: usa apenas os períodos já cadastrados nas fichas.
// - Períodos: Manhã, Dia, Noite e Conforme necessário.
// - "Conforme necessário" recebe também os óleos sem período definido.
// - Adultos e Crianças em seções separadas quando a seleção misturar.

import type { OleoProtocolo, PeriodoRotina } from './protocolosEssenciais'

export interface ItemRotina {
  nome: string
  texto: string
}

export interface BlocoRotina {
  periodo: PeriodoRotina
  titulo: string
  itens: ItemRotina[]
}

export interface SecaoRotina {
  titulo: 'Adultos' | 'Crianças'
  blocos: BlocoRotina[]
}

const TITULOS: Record<PeriodoRotina, string> = {
  manha: 'Manhã',
  dia: 'Dia',
  noite: 'Noite',
  conforme: 'Conforme necessário',
}

const ORDEM: PeriodoRotina[] = ['manha', 'dia', 'noite', 'conforme']

// Garante ponto final sem duplicar
function comPonto(texto: string): string {
  const limpo = texto.trim()
  if (!limpo) return limpo
  return /[.!?]$/.test(limpo) ? limpo : `${limpo}.`
}

// Monta os blocos de uma seção. Óleos sem período cadastrado entram em
// "Conforme necessário" mantendo o texto que a ficha já traz.
function montarBlocos(oleos: OleoProtocolo[]): BlocoRotina[] {
  const porPeriodo = new Map<PeriodoRotina, ItemRotina[]>()

  oleos.forEach((oleo) => {
    const periodos = oleo.periodos.length > 0 ? oleo.periodos : []
    if (periodos.length === 0) {
      const instrucao =
        oleo.campos.find((campo) => campo.rotulo === 'Frequência')?.texto ||
        oleo.campos.find((campo) => campo.rotulo === 'Como usar')?.texto ||
        ''
      const itens = porPeriodo.get('conforme') || []
      itens.push({
        nome: oleo.nome,
        texto: instrucao
          ? comPonto(instrucao.split('\n')[0])
          : 'Usar conforme a orientação da ficha.',
      })
      porPeriodo.set('conforme', itens)
      return
    }

    periodos.forEach(({ periodo, texto }) => {
      const itens = porPeriodo.get(periodo) || []
      itens.push({ nome: oleo.nome, texto: comPonto(texto) })
      porPeriodo.set(periodo, itens)
    })
  })

  return ORDEM.filter((periodo) => (porPeriodo.get(periodo) || []).length > 0).map(
    (periodo) => ({
      periodo,
      titulo: TITULOS[periodo],
      itens: porPeriodo.get(periodo) || [],
    })
  )
}

// Seções da rotina: uma só quando todos os óleos são do mesmo grupo,
// duas ("Adultos" e "Crianças") quando a seleção mistura.
export function montarRotinaDiaria(oleos: OleoProtocolo[]): SecaoRotina[] {
  const adultos = oleos.filter((oleo) => oleo.secao === 'Adultos')
  const criancas = oleos.filter((oleo) => oleo.secao === 'Crianças')

  if (adultos.length > 0 && criancas.length > 0) {
    return [
      { titulo: 'Adultos', blocos: montarBlocos(adultos) },
      { titulo: 'Crianças', blocos: montarBlocos(criancas) },
    ]
  }

  const unico = adultos.length > 0 ? adultos : criancas
  const titulo = criancas.length > 0 && adultos.length === 0 ? 'Crianças' : 'Adultos'
  return [{ titulo, blocos: montarBlocos(unico) }]
}

// Observação exibida no final da rotina diária desta aba
// (não é usada pela Rotina Diária do detalhe do lead nem pela do diagnóstico).
export const OBSERVACAO_ROTINA_DIARIA =
  'Observação: se você tem vários óleos relaxantes e vários energizantes, não precisa usar todos. Escolha um ou dois e alterne. Ou use por alguns dias e observe qual combina melhor com cada situação. Na dúvida, entre em contato para orientação personalizada.'

// Texto para WhatsApp: negrito com asteriscos, sem emojis, sem recuo e
// uma linha em branco entre os períodos.
export function gerarTextoRotinaDiaria(
  secoes: SecaoRotina[],
  nomeCliente?: string
): string {
  const nome = (nomeCliente || '').trim()
  const linhas: string[] = []

  linhas.push(
    nome ? `*Rotina diária de ${nome}*` : '*Rotina diária com seus óleos essenciais*'
  )

  secoes.forEach((secao) => {
    linhas.push('')
    if (secoes.length > 1) linhas.push(`*${secao.titulo}:*`)

    secao.blocos.forEach((bloco, indice) => {
      if (indice > 0 || secoes.length > 1) linhas.push('')
      linhas.push(`*${bloco.titulo}:*`)
      bloco.itens.forEach((item) => {
        linhas.push(`*${item.nome}:* ${item.texto}`)
      })
    })
  })

  linhas.push('')
  linhas.push(OBSERVACAO_ROTINA_DIARIA)

  return linhas.join('\n')
}
